import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';

import isEmpty from 'lodash-es/isEmpty';
import { debounce } from 'lodash-es/function';
import { format } from 'date-fns';

import { getInitialQueryString } from '@ecp/common/src/utils/good/goodSearchUtil';
import useSearchHistory from '@ecp/common/src/hooks/useSearchHistory';
import { FlexBox } from '@ecp/common/src/layouts/flex/styled';
import SearchInput from '@ecp/common/src/components/input/SearchInput';
import Popover from '@ecp/common/src/components/popover/Popover';
// ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
import { SITE_SKYPASS, SITE_KOREANAIR_SAVINGS } from '@ecp/common/src/const/site/siteNumberConst.js';

import { userInfoState } from '@fo-recoil/common/user/atom';
import { useAlertDialog, useConfirmDialog } from '@fo-hooks/common/useModal';
import displaySearchApi from '@fo-apis/display/displaySearchApi';
import GnbSearchBoard from '@fo-pages/home/gnb/search/GnbSearchBoard';
import GnbSearchPhrase from '@fo-pages/home/gnb/search/GnbSearchPhrase';
import { useSiteComponentResolver } from '@ecp/fo/src/hooks/resolver/SiteComponentResolverHook';
import useSiteInfo from '@fo-hooks/common/useSiteInfo';

const GnbSearch = ({ searchPhraseList, searchPraseListIndex }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const userInfo = useRecoilValue(userInfoState);
  const { addSearchKeyword, removeSearchKeyword, removeAllSearchKeyword, getRecommendSearchKeywordList, getSearchKeywordList } =
    useSearchHistory(userInfo?.userId);

  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [keywordList, setKeywordList] = useState([]);
  const [keywordHistoryList, setKeywordHistoryList] = useState([]);
  const [realTimeKeywordList, setRealTimeKeywordList] = useState(null);
  const [referenceTime, setReferenceTime] = useState(null);

  const { showAlertDialog } = useAlertDialog();
  const { showConfirmDialog } = useConfirmDialog();
  const { siteNumber } = useSiteInfo();
  const showInput = useMemo(() => {
    return searchPhraseList?.length === 0 || open || value;
  }, [open, searchPhraseList?.length, value]);

  const fetchAutoCompleteSearchTermList = async (value) => {
    const { result } = await displaySearchApi.getAutoCompleteSearchTermList(value);
    setKeywordList(result);
    if (result?.length > 0) {
      setOpen(true);
    }
  };

  // ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
  const isSkypass = useMemo(() => siteNumber === SITE_SKYPASS || siteNumber === SITE_KOREANAIR_SAVINGS, [siteNumber]);

  const handleAutoComplete = useMemo(
    () =>
      debounce(async (value) => {
        // ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
        if (siteNumber !== SITE_SKYPASS || siteNumber !== SITE_KOREANAIR_SAVINGS) {
          await fetchAutoCompleteSearchTermList(value);
        }
      }, 200),
    []
  );

  const handleChange = useCallback(
    async (e) => {
        if (!open && !e?.target?.value) {
          setOpen(true);
        }

        setValue(e?.target?.value ?? '');
        const searchTerm = e?.target?.value?.trim?.();
        if (searchTerm) {
          setOpen(false);
          await handleAutoComplete(searchTerm);
        } else {
          setKeywordList([]);
        }
    },
    [handleAutoComplete, open]
  );
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setKeywordList([]);
      return;
    }

    setKeywordHistoryList(getSearchKeywordList());
  }, [getSearchKeywordList, open]);

  useEffect(() => {
    if (location.pathname === '/search') {
      setValue(searchParams.get('searchTerm'));
    } else {
      setValue('');
    }
    setOpen(false);
  }, [location.pathname, searchParams]);

  const handleSearch = () => {
    keywordSearch();
  };

  const handleSearchClick = () => {
    keywordSearch();
  };

  const keywordSearch = () => {
    const searchTerm = value?.trim?.();
    if (isEmpty(searchTerm)) {
      showAlertDialog('검색어를 입력해 주세요.');
    } else {
      const updatedList = addSearchKeyword(searchTerm);
      setKeywordHistoryList(updatedList);

      navigate({
        pathname: '/search',
        search: getInitialQueryString({
          searchTerm: searchTerm,
          countPerPage: 60,
        }),
      });

      handleClose();
    }
  };

  const handleDeleteKeywordHistory = (keyword) => {
    const updatedList = removeSearchKeyword(keyword);
    setKeywordHistoryList(updatedList);
  };

  const handleDeleteAllKeywordHistory = () => {
    showConfirmDialog('검색어를 전체 삭제 하시겠습니까?', () => {
      removeAllSearchKeyword();
      setKeywordHistoryList([]);
      setValue('');
      setOpen(true);
    });
  };

  const handleClickSearchPhrase = ({ pcUrl, target }) => {
    window.open(pcUrl, target);
  };

  const handleChangeMode = async () => {
    if (value) {
      setOpen(false);
      // ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
      if (siteNumber !== SITE_SKYPASS || siteNumber !== SITE_KOREANAIR_SAVINGS) {
        await fetchAutoCompleteSearchTermList(value);
      }
      return;
    }
    if (!open) {
      setOpen(true);
    }
  };

  const recommendKeywordList = useMemo(() => getRecommendSearchKeywordList(), [getRecommendSearchKeywordList]);

  const fetchRealTimeKeywordList = useCallback(async () => {
    try {
      const { result } = await displaySearchApi.getTrendSearchTermList();
      const mappedKeywordList = result.map((it, index) => ({ ...it, ranking: index + 1 }));
      setRealTimeKeywordList(mappedKeywordList);
      setReferenceTime(format(new Date(), 'yyyy.MM.dd HH:mm'));
    } catch (error) {
      setRealTimeKeywordList([]);
      setReferenceTime(null);
    }
  }, []);

  useEffect(() => {
    if (!realTimeKeywordList) {
      (async () => await fetchRealTimeKeywordList())();
    }
  }, [fetchRealTimeKeywordList, realTimeKeywordList]);

  const ResolvedSearchInput = useSiteComponentResolver(
    <SearchInput
      clearable
      autoFocus
      type={'main'}
      placeholder={'검색어를 입력해 주세요.'}
      title={'검색어'}
      value={value}
      onChange={handleChange}
      onSearchClick={handleSearchClick}
      onEnter={handleSearch}
      onClick={handleChangeMode}
      suffixText={'검색'}
    />,
    {
      // 대한항공
      [SITE_SKYPASS]: (
        <SearchInput
          clearable
          autoFocus
          maxLength={100}
          type={'gnb'}
          placeholder={'상품 검색어를 입력해 주세요'}
          title={'검색어'}
          value={value}
          onChange={handleChange}
          onSearchClick={handleSearchClick}
          onEnter={handleSearch}
          onClick={handleChangeMode}
          suffixText={'검색'}
          isSkypass={isSkypass}
        />
      ),
      // ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
      [SITE_KOREANAIR_SAVINGS]: (
        <SearchInput
          clearable
          autoFocus
          maxLength={100}
          type={'gnb'}
          placeholder={'상품 검색어를 입력해 주세요'}
          title={'검색어'}
          value={value}
          onChange={handleChange}
          onSearchClick={handleSearchClick}
          onEnter={handleSearch}
          onClick={handleChangeMode}
          suffixText={'검색'}
          isSkypass={isSkypass}
        />
      ),
    }
  );

  return (
    <>
      {showInput ? (
        <Popover
          closeWhenOutside
          closeWhenScroll
          placement={'bottom-start'}
          offset={{ mainAxis: 11, crossAxis: 0 }}
          open={open}
          value={
            <GnbSearchBoard
              keyword={value}
              keywordList={keywordList}
              recommendKeywordList={recommendKeywordList}
              keywordHistoryList={keywordHistoryList}
              onClose={handleClose}
              setKeywordHistoryList={setKeywordHistoryList}
              handleDeleteKeywordHistory={handleDeleteKeywordHistory}
              handleDeleteAllKeywordHistory={handleDeleteAllKeywordHistory}
              referenceTime={referenceTime}
              realTimeKeywordList={realTimeKeywordList}
            />
          }
          onOpenChange={handleClose}
          blockAutoFocus
        >
          <FlexBox width={'500px'}>{ResolvedSearchInput}</FlexBox>
        </Popover>
      ) : (
        <GnbSearchPhrase
          searchPhraseList={searchPhraseList}
          searchPraseListIndex={searchPraseListIndex}
          handleChangeMode={handleChangeMode}
          handleClickSearch={handleClickSearchPhrase}
        />
      )}
    </>
  );
};

export default GnbSearch;

GnbSearch.propTypes = {
  searchPhraseList: PropTypes.array,
  searchPraseListIndex: PropTypes.number,
};
